/**
 * Central enum label mappings for int parameters that use the select (dropdown) UI.
 * Used by NodeBody (EnumSelector) and OverlayManager dropdown so the same labels appear.
 */

import { LUT_PRESET_META } from '../shaders/colorRamps/lutPresetData.generated';

const COLOR_LUT_PRESET_LABELS: Record<number, string> = Object.fromEntries(
  LUT_PRESET_META.map((p) => [p.index, p.label])
);

export function isEnumParameter(specId: string, paramName: string): boolean {
  return getParameterEnumMappings(specId, paramName) !== null;
}

/** Enum params that show prev/next chevrons beside the dropdown (quick cycle). */
export function shouldShowEnumSteppers(specId: string, paramName: string): boolean {
  return (
    (specId === 'color-lut' && paramName === 'preset') ||
    (specId === 'blend' && paramName === 'mode')
  );
}

export function getParameterEnumMappings(
  nodeId: string,
  paramName: string
): Record<number, string> | null {
  // compare node - operation
  if (nodeId === 'compare' && paramName === 'operation') {
    return {
      0: 'Equal (==)',
      1: 'Not Equal (!=)',
      2: 'Less Than (<)',
      3: 'Less or Equal (<=)',
      4: 'Greater Than (>)',
      5: 'Greater or Equal (>=)'
    };
  }

  if (nodeId === 'blend' && paramName === 'mode') {
    return {
      0: 'Normal',
      1: 'Multiply',
      2: 'Screen',
      3: 'Overlay',
      4: 'Soft Light',
      5: 'Hard Light',
      6: 'Color Dodge',
      7: 'Color Burn',
      8: 'Linear Dodge',
      9: 'Linear Burn',
      10: 'Difference',
      11: 'Exclusion'
    };
  }

  if (nodeId === 'blend' && paramName === 'alphaMode') {
    return {
      0: 'Lerp',
      1: 'Blend'
    };
  }

  // gradient-mask node - maskType
  if (nodeId === 'gradient-mask' && paramName === 'maskType') {
    return {
      0: 'Radial',
      1: 'Linear',
      2: 'Elliptical'
    };
  }

  // block-edge-brightness node - direction
  if (nodeId === 'block-edge-brightness' && paramName === 'direction') {
    return { 0: 'Horizontal', 1: 'Vertical' };
  }

  // block-color-glitch node - direction
  if (nodeId === 'block-color-glitch' && paramName === 'direction') {
    return { 0: 'Horizontal', 1: 'Vertical' };
  }

  // plane-grid node - planeType
  if (nodeId === 'plane-grid' && paramName === 'planeType') {
    return {
      0: 'Raymarched',
      1: 'Grid',
      2: 'Checkerboard'
    };
  }

  // box-torus-sdf node - primitiveType
  if (nodeId === 'box-torus-sdf' && paramName === 'primitiveType') {
    return {
      0: 'Box',
      1: 'Torus',
      2: 'Capsule',
      3: 'Cylinder',
      4: 'Cone',
      5: 'Round Cone',
      6: 'Octahedron',
      7: 'Icosahedron'
    };
  }

  // box-torus-sdf node - mode (directional vs point light)
  if (nodeId === 'box-torus-sdf' && paramName === 'mode') {
    return { 0: 'Directional', 1: 'Point' };
  }

  // voronoi-noise
  if (nodeId === 'voronoi-noise' && paramName === 'voronoiDistanceMetric') {
    return { 0: 'Euclidean', 1: 'Manhattan', 2: 'Chebyshev' };
  }
  if (nodeId === 'voronoi-noise' && paramName === 'voronoiAnimationMode') {
    return { 0: 'Drift', 1: 'Rotate', 2: 'Still' };
  }
  if (nodeId === 'voronoi-noise' && paramName === 'voronoiOutputMode') {
    return { 0: 'F1', 1: 'F2−F1', 2: 'Edge', 3: 'Cell ID' };
  }

  // gradient
  if (nodeId === 'gradient' && paramName === 'gradientType') {
    return { 0: 'Radial', 1: 'Linear' };
  }

  if (nodeId === 'oklch-color-map' && paramName === 'mapMode') {
    return { 0: 'Smooth', 1: 'Stepped' };
  }

  if (nodeId === 'color-lut' && paramName === 'preset') {
    return COLOR_LUT_PRESET_LABELS;
  }

  if (nodeId === 'color-gradient' && paramName === 'gradientMode') {
    return { 0: 'Radial', 1: 'Linear' };
  }

  // shapes-2d
  if (nodeId === 'shapes-2d' && paramName === 'shapeType') {
    return {
      0: 'Circle / Ellipse',
      1: 'Square / Box (rounded)',
      2: 'Superellipse',
      3: 'Regular polygon',
      4: 'Capsule',
    };
  }

  // hex-voxel - shapeType
  if (nodeId === 'hex-voxel' && paramName === 'shapeType') {
    return { 0: 'Boxy', 1: 'Sphere minus box', 2: 'Heightmap' };
  }

  // worley-noise
  if (nodeId === 'worley-noise' && paramName === 'worleyDistanceMetric') {
    return { 0: 'Euclidean', 1: 'Manhattan', 2: 'Chebyshev' };
  }
  if (nodeId === 'worley-noise' && paramName === 'worleyOutputMode') {
    return { 0: 'F1', 1: 'F2−F1', 2: 'Edge' };
  }

  // reaction-diffusion
  if (nodeId === 'reaction-diffusion' && paramName === 'steps') {
    return { 1: '1 Step', 2: '2 Steps', 3: '3 Steps', 4: '4 Steps', 5: '5 Steps' };
  }

  // noise
  if (nodeId === 'noise' && paramName === 'noiseMode') {
    return { 0: 'Simplex 2D', 1: 'Simplex 3D', 2: 'Value fBm' };
  }

  // fractal
  if (nodeId === 'fractal' && paramName === 'fractalMode') {
    return {
      0: 'KIFS',
      1: 'Kaleidoscope',
      2: 'Cross',
      3: 'Escape',
      4: 'Orbit trap',
      5: 'Min distance',
      6: 'Newton',
      7: 'Lyapunov',
      8: 'Shape Julia'
    };
  }

  if (nodeId === 'fractal' && paramName === 'fractalPortalEnable') {
    return {
      0: 'Off',
      1: 'On'
    };
  }

  if (nodeId === 'fractal' && paramName === 'fractalEscapeFamily') {
    return {
      0: 'Julia',
      1: 'Mandelbrot',
      2: 'Burning Ship'
    };
  }

  if (nodeId === 'fractal' && paramName === 'fractalColoring') {
    return {
      0: 'Iteration',
      1: 'Smooth',
      2: 'Distance'
    };
  }

  if (nodeId === 'fractal' && paramName === 'fractalTrapShape') {
    return {
      0: 'Ring',
      1: 'Line',
      2: 'Cross',
      3: 'Spiral',
      4: 'Multi'
    };
  }

  // stripes (and legacy wave-patterns id)
  if (
    (nodeId === 'stripes' || nodeId === 'wave-patterns') &&
    paramName === 'waveType'
  ) {
    return { 0: 'Sine', 1: 'Cosine', 2: 'Square', 3: 'Triangle' };
  }

  // mixed-wave-signal (per-wave shape; same labels for w0Shape / w1Shape / w2Shape)
  if (
    nodeId === 'mixed-wave-signal' &&
    (paramName === 'w0Shape' || paramName === 'w1Shape' || paramName === 'w2Shape')
  ) {
    return {
      0: 'Sine',
      1: 'Cosine',
      2: 'Square',
      3: 'Triangle',
      4: 'Saw up',
      5: 'Saw down',
      6: 'Abs sine',
      7: 'Smooth square',
    };
  }

  // blur
  if (nodeId === 'blur' && paramName === 'blurType') {
    return { 0: 'Gaussian', 1: 'Directional', 2: 'Radial' };
  }

  // particle-system
  if (nodeId === 'particle-system' && paramName === 'particleCount') {
    return { 1: '1', 2: '2', 3: '3', 4: '4' };
  }

  // lighting-shading
  if (nodeId === 'lighting-shading' && paramName === 'lightType') {
    return { 0: 'Directional', 1: 'Point' };
  }

  // combine-vector (utility) - outputType 2=vec2, 3=vec3, 4=vec4
  if (nodeId === 'combine-vector' && paramName === 'outputType') {
    return { 2: 'vec2', 3: 'vec3', 4: 'vec4' };
  }

  // ripple
  if (nodeId === 'ripple' && paramName === 'rippleMode') {
    return { 0: 'Concentric', 1: 'Directional' };
  }

  // path-drive - path preset
  if (nodeId === 'path-drive' && paramName === 'pathPreset') {
    return {
      0: 'Orbit',
      1: 'Figure-8',
      2: 'Drift',
      3: 'Pulse',
      4: 'Line',
    };
  }

  // wake-smear - path preset (line, orbit, figure-8, drift)
  if (nodeId === 'wake-smear' && paramName === 'wakeSmearPathPreset') {
    return {
      0: 'Line',
      1: 'Orbit',
      2: 'Figure-8',
      3: 'Drift',
    };
  }

  // circle-inversion - layout preset (triangle, chain, flower, random)
  if (nodeId === 'circle-inversion' && paramName === 'circleInversionLayoutPreset') {
    return {
      0: 'Triangle',
      1: 'Chain',
      2: 'Flower',
      3: 'Random',
    };
  }

  // oscillator-2d - layerCombine (same merge rule on X and Y stacks)
  if (nodeId === 'oscillator-2d' && paramName === 'layerCombine') {
    return {
      0: 'Sum',
      1: 'Normalized',
      2: 'Product',
      3: 'Max |·|',
    };
  }

  // glass-shell - outer / inner SDF shapes
  if (nodeId === 'glass-shell' && paramName === 'outerShape') {
    return {
      0: 'Sphere',
      1: 'Box',
      2: 'Icosahedron',
    };
  }
  if (nodeId === 'glass-shell' && paramName === 'innerShape') {
    return {
      0: 'Sphere',
      1: 'Box',
      2: 'Sphere + box (smooth)',
    };
  }

  // triangle-grid
  if (nodeId === 'triangle-grid' && paramName === 'triProjection') {
    return { 0: 'Infinite plane', 1: 'UV' };
  }

  // plane-project
  if (nodeId === 'plane-project' && paramName === 'cameraSource') {
    return { 0: 'Built-in', 1: 'External' };
  }
  if (nodeId === 'plane-project' && paramName === 'uvMode') {
    return { 0: 'World', 1: 'Normalized', 2: 'Centered' };
  }
  if (nodeId === 'plane-project' && paramName === 'clipRect') {
    return { 0: 'Off', 1: 'On' };
  }

  // radial-uv-warp
  if (nodeId === 'radial-uv-warp' && paramName === 'warpMode') {
    return {
      0: 'Bulge / pinch',
      1: 'Fisheye',
      2: 'Spherize',
    };
  }

  // displace
  if (nodeId === 'displace' && paramName === 'displaceMode') {
    return { 0: 'Vector offset', 1: 'Directional' };
  }

  if (nodeId === 'pixelize' && paramName === 'pixelizeSpace') {
    return { 0: 'UV', 1: 'Screen' };
  }

  if (nodeId === 'pixelize' && paramName === 'pixelizeSnap') {
    return { 0: 'Floor', 1: 'Center', 2: 'Round' };
  }

  // uv-band-shift
  if (nodeId === 'uv-band-shift' && paramName === 'uvBandShiftOrientation') {
    return { 0: 'Horizontal', 1: 'Vertical' };
  }

  // infinite-zoom
  if (nodeId === 'infinite-zoom' && paramName === 'infiniteZoomMotion') {
    return {
      0: 'Ping-pong loop',
      1: 'Snap zoom in',
      2: 'Snap zoom out',
    };
  }

  // bloom-sphere
  if (nodeId === 'bloom-sphere' && paramName === 'mode') {
    return { 0: 'Lattice Glow', 1: 'Legacy Bloom' };
  }

  if (nodeId === 'arrangement-lanes') {
    if (paramName === 'uvInputMode') {
      return { 0: 'Normalized', 1: 'UV Coords' };
    }
    if (paramName === 'viewportMode') {
      return { 0: 'Follow', 1: 'Fixed' };
    }
  }

  if (nodeId === 'arrangement-notes') {
    if (paramName === 'trackLayout') {
      return { 0: 'Overlap', 1: 'Lanes' };
    }
    if (paramName === 'layoutOrientation') {
      return { 0: 'Horizontal', 1: 'Vertical' };
    }
    if (paramName === 'playheadShow') {
      return { 0: 'Off', 1: 'On' };
    }
    if (paramName === 'timelineAnchor') {
      return { 0: 'Center', 1: 'Start' };
    }
    if (paramName === 'noteColorMode') {
      return {
        0: 'Light',
        1: 'Dark',
        2: 'Project',
        3: 'Custom',
        4: 'Pitch',
      };
    }
  }

  if (nodeId === 'arrangement-lanes' && paramName === 'colorSource') {
    return { 0: 'Palette', 1: 'DAW' };
  }

  if (nodeId === 'velocity-spark-grid') {
    if (paramName === 'shape') {
      return { 0: 'Dot', 1: 'Cell' };
    }
    if (paramName === 'blendMode') {
      return { 0: 'Max', 1: 'Add' };
    }
    if (paramName === 'decayCurve') {
      return { 0: 'Exp', 1: 'Linear' };
    }
  }

  if (nodeId === 'note-gravity-warp') {
    if (paramName === 'decayCurve') {
      return { 0: 'Exp', 1: 'Linear' };
    }
    if (paramName === 'blendMode') {
      return { 0: 'Sum', 1: 'Max', 2: 'Avg' };
    }
  }

  return null;
}
